import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast, Toaster } from 'sonner';
import { Plus, Edit2, Trash2, X, Save, ChevronDown, ChevronUp, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const sectionTypes = ['hero', 'story', 'values', 'differentiators', 'tech_stack', 'partners'];
const icons = ['Key', 'Target', 'Brain', 'Shield', 'Users', 'Award', 'Zap', 'Lock', 'Globe', 'Monitor', 'Smartphone', 'Server', 'Cloud', 'Briefcase', 'Calculator', 'Scale', 'Heart'];

const AdminAboutPage = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [formData, setFormData] = useState({
    section_type: 'values',
    title: '',
    subtitle: '',
    content: '',
    image_url: '',
    items: [],
    order: 0
  });
  const [newItem, setNewItem] = useState({ icon: '', title: '', description: '' });

  useEffect(() => { loadSections(); }, []);

  const loadSections = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/cms/about?active_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSections(data || []);
    } catch (e) {
      toast.error('Failed to load about sections');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingId 
        ? `${API_URL}/api/cms/about/${editingId}`
        : `${API_URL}/api/cms/about`;
      
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) throw new Error('Failed to save');
      toast.success(editingId ? 'Section updated' : 'Section created');
      resetForm();
      loadSections();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/cms/about/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Section deleted');
      loadSections();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (section) => {
    setFormData({
      section_type: section.section_type,
      title: section.title || '',
      subtitle: section.subtitle || '',
      content: section.content || '',
      image_url: section.image_url || '',
      items: section.items || [],
      order: section.order || 0
    });
    setEditingId(section.id);
    setShowForm(true);
  };

  const handleToggleActive = async (section) => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/cms/about/${section.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !section.is_active })
      });
      toast.success(`Section ${section.is_active ? 'hidden' : 'shown'}`);
      loadSections();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const addItem = () => {
    if (newItem.title.trim()) {
      setFormData(prev => ({ 
        ...prev, 
        items: [...prev.items, { ...newItem }] 
      }));
      setNewItem({ icon: '', title: '', description: '' });
    }
  };

  const removeItem = (index) => {
    setFormData(prev => ({ 
      ...prev, 
      items: prev.items.filter((_, i) => i !== index) 
    }));
  };

  const resetForm = () => {
    setFormData({
      section_type: 'values',
      title: '',
      subtitle: '',
      content: '',
      image_url: '',
      items: [],
      order: 0
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getSectionTypeLabel = (type) => {
    const labels = {
      hero: 'Hero Section',
      story: 'Our Story',
      values: 'Core Values',
      differentiators: 'Differentiators',
      tech_stack: 'Tech Stack',
      partners: 'Partner Program'
    };
    return labels[type] || type;
  };

  if (loading) return <div className="text-text-secondary">Loading...</div>;

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">About Page</h1>
          <p className="text-text-secondary mt-1">Manage About page sections and content</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-tech-blue hover:bg-tech-blue/90">
          <Plus size={18} className="mr-2" />
          Add Section
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl border-border-line my-8 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between pb-3 sticky top-0 bg-surface z-10">
              <CardTitle>{editingId ? 'Edit' : 'Add'} Section</CardTitle>
              <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
                <X size={20} />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Section Type *</label>
                    <select
                      required
                      value={formData.section_type}
                      onChange={(e) => setFormData({ ...formData, section_type: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    >
                      {sectionTypes.map(type => (
                        <option key={type} value={type}>{getSectionTypeLabel(type)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Subtitle</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Content</label>
                  <textarea
                    rows={4}
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                    placeholder="Main content text..."
                  />
                </div>

                <div>
                  <label className="block text-sm text-text-secondary mb-2">Image URL</label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    placeholder="https://..."
                  />
                </div>

                {/* Items Section */}
                <div className="border border-border-line rounded-lg p-4">
                  <label className="block text-sm font-medium text-text-primary mb-3">Items (Values, Tech Stack, etc.)</label>
                  
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <select
                      value={newItem.icon}
                      onChange={(e) => setNewItem({ ...newItem, icon: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                    >
                      <option value="">Icon</option>
                      {icons.map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={newItem.title}
                      onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                      placeholder="Title"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newItem.description}
                        onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                        className="flex-1 px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                        placeholder="Description"
                      />
                      <Button type="button" onClick={addItem} variant="outline" size="sm">
                        <Plus size={16} />
                      </Button>
                    </div>
                  </div>

                  {formData.items.length > 0 && (
                    <ul className="space-y-2 max-h-40 overflow-y-auto">
                      {formData.items.map((item, index) => (
                        <li key={index} className="flex items-center justify-between p-2 bg-page-bg rounded text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="text-tech-blue text-xs px-2 py-1 bg-tech-blue/10 rounded">{item.icon || 'No icon'}</span>
                            <span className="font-medium text-text-primary">{item.title}</span>
                            {item.description && <span className="text-text-secondary truncate max-w-[200px]">- {item.description}</span>}
                          </div>
                          <button type="button" onClick={() => removeItem(index)} className="text-red-500 hover:text-red-600">
                            <X size={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                    <Save size={18} className="mr-2" />
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sections List */}
      <div className="space-y-4">
        {sections.sort((a, b) => a.order - b.order).map((section) => (
          <Card 
            key={section.id} 
            className={`border-border-line ${!section.is_active ? 'opacity-50' : ''}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-xs text-text-secondary">#{section.order}</span>
                    <span className="text-xs px-2 py-1 rounded bg-tech-blue/10 text-tech-blue font-medium">
                      {getSectionTypeLabel(section.section_type)}
                    </span>
                    <button
                      onClick={() => handleToggleActive(section)}
                      className={`text-xs px-2 py-1 rounded ${section.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {section.is_active ? 'Active' : 'Hidden'}
                    </button>
                  </div>
                  <h3 className="font-semibold text-text-primary text-lg">{section.title}</h3>
                  {section.subtitle && (
                    <p className="text-sm text-text-secondary mt-1">{section.subtitle}</p>
                  )}
                  {section.items && section.items.length > 0 && (
                    <p className="text-xs text-text-secondary mt-2">{section.items.length} items</p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                    className="p-2 text-text-secondary hover:text-tech-blue"
                  >
                    {expandedSection === section.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  <button onClick={() => handleEdit(section)} className="p-2 text-text-secondary hover:text-tech-blue">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => handleDelete(section.id)} className="p-2 text-text-secondary hover:text-red-500">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedSection === section.id && (
                <div className="mt-4 pt-4 border-t border-border-line">
                  {section.content && (
                    <div className="mb-3">
                      <label className="text-xs text-text-secondary">Content:</label>
                      <p className="text-sm text-text-primary whitespace-pre-wrap">{section.content}</p>
                    </div>
                  )}
                  {section.image_url && (
                    <div className="mb-3">
                      <label className="text-xs text-text-secondary">Image:</label>
                      <img src={section.image_url} alt="" className="h-20 rounded mt-1" />
                    </div>
                  )}
                  {section.items && section.items.length > 0 && (
                    <div>
                      <label className="text-xs text-text-secondary">Items:</label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {section.items.map((item, idx) => (
                          <div key={idx} className="p-2 bg-page-bg rounded text-sm">
                            <div className="flex items-center space-x-2">
                              {item.icon && <span className="text-tech-blue text-xs">[{item.icon}]</span>}
                              <span className="font-medium">{item.title || item.type || item.category}</span>
                            </div>
                            {item.description && <p className="text-xs text-text-secondary mt-1">{item.description}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {sections.length === 0 && (
        <div className="text-center py-12 text-text-secondary">
          <Info className="mx-auto mb-4" size={48} />
          <p>No sections yet. Click "Add Section" to create one.</p>
        </div>
      )}
    </div>
  );
};

export default AdminAboutPage;
